import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Eye, EyeOff } from 'lucide-react';

// Defined OUTSIDE component — avoids React remount on every keystroke
const PassField = ({ label, fieldKey, form, setForm }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          required
          value={form[fieldKey] || ''}
          onChange={e => setForm(prev => ({ ...prev, [fieldKey]: e.target.value }))}
          className="input-dark pr-9"
          style={{ width: '100%' }}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2"
          style={{ color: '#475569', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
};

export default function ChangePassword() {
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { changePassword, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.newPass !== form.confirm) return setError('New passwords do not match');
    if (form.newPass.length < 8) return setError('Password must be at least 8 characters');
    if (form.newPass === form.current) return setError('New password must be different from current password');
    setError(''); setLoading(true);
    try {
      await changePassword(form.current, form.newPass);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-mesh flex items-center justify-center p-4">
      <div className="w-full max-w-sm" style={{ animation: 'slideUp 0.5s ease-out' }}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)' }}>
            <Lock size={28} style={{ color: '#60a5fa' }} />
          </div>
          <h1 className="text-xl font-bold gradient-text">
            {user?.must_change_password ? 'Set Your Password' : 'Change Password'}
          </h1>
          {user?.must_change_password && (
            <p className="text-sm mt-1" style={{ color: '#334155' }}>
              Please set a new password to continue
            </p>
          )}
        </div>

        <div className="glass-bright rounded-2xl p-7" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl text-sm"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <PassField label="Current Password" fieldKey="current" form={form} setForm={setForm} />
            <PassField label="New Password" fieldKey="newPass" form={form} setForm={setForm} />
            <PassField label="Confirm New Password" fieldKey="confirm" form={form} setForm={setForm} />

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5 mt-2">
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
